import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { CalendarEvent, CalendarState, CalendarView } from '../../types/calendar';
import { v4 as uuidv4 } from 'uuid';

// Helper function to load events from localStorage
const loadEventsFromStorage = (): CalendarEvent[] => {
  try {
    const stored = localStorage.getItem('calendar-events');
    if (!stored) return [];

    const events: CalendarEvent[] = JSON.parse(stored);

    // Migration: Remove delivery events without orderId (orphaned from old schema)
    const validEvents = events.filter((e: CalendarEvent) => {
      if (e.title?.startsWith('Delivery:') && !e.orderId) {
        console.warn('[Migration] Removing orphaned delivery event without orderId:', e.title);
        return false; // Remove this event
      }
      return true; // Keep all other events
    });

    return validEvents;
  } catch (error) {
    console.error('Error loading calendar events from localStorage:', error);
    return [];
  }
};

// Helper function to get today's date in ISO format
const getTodayISO = (): string => {
  const today = new Date();
  return today.toISOString().split('T')[0];
};

// Helper function to create a blank event
export const createBlankEvent = (): CalendarEvent => {
  const now = new Date().toISOString();
  const today = getTodayISO();

  return {
    id: uuidv4(),
    title: '',
    description: '',
    startDate: today,
    endDate: today,
    startTime: '09:00',
    endTime: '10:00',
    color: '#2196F3',
    allDay: false,
    createdAt: now,
    updatedAt: now
  };
};

const initialState: CalendarState = {
  events: loadEventsFromStorage(),
  currentView: 'month',
  currentDate: getTodayISO()
};

const calendarSlice = createSlice({
  name: 'calendar',
  initialState,
  reducers: {
    addEvent: (state, action: PayloadAction<CalendarEvent>) => {
      state.events.push(action.payload);
    },
    updateEvent: (state, action: PayloadAction<CalendarEvent>) => {
      const index = state.events.findIndex(event => event.id === action.payload.id);
      if (index !== -1) {
        state.events[index] = {
          ...action.payload,
          updatedAt: new Date().toISOString()
        };
      }
    },
    deleteEvent: (state, action: PayloadAction<string>) => {
      state.events = state.events.filter(event => event.id !== action.payload);
    },
    setCurrentView: (state, action: PayloadAction<CalendarView>) => {
      state.currentView = action.payload;
    },
    setCurrentDate: (state, action: PayloadAction<string>) => {
      state.currentDate = action.payload;
    },
    navigateToToday: state => {
      state.currentDate = getTodayISO();
    },
    navigateNext: state => {
      switch (state.currentView) {
        case 'month': {
          // Parse date parts from ISO string to avoid timezone issues
          const [yearStr, monthStr, dayStr] = state.currentDate.split('-');
          let year = parseInt(yearStr, 10);
          let month = parseInt(monthStr, 10) - 1; // Convert to 0-indexed
          const day = parseInt(dayStr, 10);

          // Increment month
          month++;
          if (month > 11) {
            month = 0;
            year++;
          }

          // Get last day of target month to handle edge cases
          const lastDayOfTargetMonth = new Date(year, month + 1, 0).getDate();
          const targetDay = Math.min(day, lastDayOfTargetMonth);

          // Format back to ISO string
          const monthFormatted = String(month + 1).padStart(2, '0');
          const dayFormatted = String(targetDay).padStart(2, '0');
          state.currentDate = `${year}-${monthFormatted}-${dayFormatted}`;
          break;
        }
        case 'week': {
          const currentDate = new Date(state.currentDate + 'T00:00:00');
          currentDate.setDate(currentDate.getDate() + 7);
          state.currentDate = currentDate.toISOString().split('T')[0];
          break;
        }
        case 'day': {
          const currentDate = new Date(state.currentDate + 'T00:00:00');
          currentDate.setDate(currentDate.getDate() + 1);
          state.currentDate = currentDate.toISOString().split('T')[0];
          break;
        }
      }
    },
    navigatePrevious: state => {
      switch (state.currentView) {
        case 'month': {
          // Parse date parts from ISO string to avoid timezone issues
          const [yearStr, monthStr, dayStr] = state.currentDate.split('-');
          let year = parseInt(yearStr, 10);
          let month = parseInt(monthStr, 10) - 1; // Convert to 0-indexed
          const day = parseInt(dayStr, 10);

          // Decrement month
          month--;
          if (month < 0) {
            month = 11;
            year--;
          }

          // Get last day of target month to handle edge cases
          const lastDayOfTargetMonth = new Date(year, month + 1, 0).getDate();
          const targetDay = Math.min(day, lastDayOfTargetMonth);

          // Format back to ISO string
          const monthFormatted = String(month + 1).padStart(2, '0');
          const dayFormatted = String(targetDay).padStart(2, '0');
          state.currentDate = `${year}-${monthFormatted}-${dayFormatted}`;
          break;
        }
        case 'week': {
          const currentDate = new Date(state.currentDate + 'T00:00:00');
          currentDate.setDate(currentDate.getDate() - 7);
          state.currentDate = currentDate.toISOString().split('T')[0];
          break;
        }
        case 'day': {
          const currentDate = new Date(state.currentDate + 'T00:00:00');
          currentDate.setDate(currentDate.getDate() - 1);
          state.currentDate = currentDate.toISOString().split('T')[0];
          break;
        }
      }
    },
    toggleEventCompletion: (state, action: PayloadAction<string>) => {
      const event = state.events.find(e => e.id === action.payload);
      if (event) {
        event.completed = !event.completed;
        event.updatedAt = new Date().toISOString();
      }
    },
    clearAllEvents: state => {
      state.events = [];
    }
  }
});

export const {
  addEvent,
  updateEvent,
  deleteEvent,
  setCurrentView,
  setCurrentDate,
  navigateToToday,
  navigateNext,
  navigatePrevious,
  toggleEventCompletion,
  clearAllEvents
} = calendarSlice.actions;

export default calendarSlice.reducer;
